import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { reservationApi } from '@/api/reservation'
import { isTokenExpired } from '@/utils/jwt'
import { BLOCKED_WHEN_SMART_DINE_IN_ONLY, SMART_DINE_IN_ONLY } from '@/featureFlags'
import './types'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: false, showBottomNav: false },
  },
  {
    path: '/auth/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    redirect: '/auth/login',
  },
  {
    path: '/register',
    redirect: '/auth/register',
  },
  {
    path: '/restaurant-menu-view/:tenantId',
    name: 'RestaurantMenu',
    component: () => import('@/views/restaurant/MenuView.vue'),
    meta: { requiresAuth: false, showBottomNav: true },
  },
  {
    path: '/menu-view/:tenantId',
    name: 'RestaurantMenuImages',
    component: () => import('@/views/restaurant/MenuImagesView.vue'),
    meta: { requiresAuth: false, hideHeader: true, showBottomNav: false, title: 'Digital Menu Images' },
  },
  {
    path: '/restaurant/:tenantId',
    redirect: to => {
      return { path: `/restaurant-menu-view/${to.params.tenantId}`, query: to.query }
    },
  },
  {
    path: '/restaurant/:tenantId/menu',
    redirect: to => {
      return { path: `/restaurant-menu-view/${to.params.tenantId}`, query: to.query }
    },
  },
  {
    path: '/my-orders',
    name: 'MyOrders',
    component: () => import('@/views/orders/MyOrders.vue'),
    meta: { requiresAuth: true, showBottomNav: true, hideHeader: true },
  },
  {
    path: '/my-reservations',
    name: 'MyReservations',
    component: () => import('@/views/reservations/MyReservations.vue'),
    meta: { requiresAuth: true, showBottomNav: true, hideHeader: true },
  },
  {
    path: '/reservations',
    name: 'Reservations',
    component: () => import('@/views/reservations/ReservationSearch.vue'),
    meta: { requiresAuth: false, showBottomNav: true, showMobileMenu: true },
  },
  {
    path: '/reservations/:tenantId',
    name: 'ReservationRestaurant',
    component: () => import('@/views/reservations/ReservationRestaurantDetail.vue'),
    meta: { requiresAuth: false, hideHeader: true },
  },
  {
    path: '/reservations/:tenantId/confirm',
    name: 'ReservationConfirmation',
    component: () => import('@/views/reservations/ReservationConfirmation.vue'),
    meta: { requiresAuth: false, hideHeader: true },
  },
  {
    path: '/reservations/:tenantId/success',
    name: 'ReservationSuccess',
    component: () => import('@/views/reservations/ReservationSuccess.vue'),
    meta: { requiresAuth: false, hideHeader: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/profile/ProfileView.vue'),
    meta: { requiresAuth: true, showBottomNav: true },
  },
  {
    path: '/restaurants',
    name: 'Restaurants',
    component: () => import('@/views/restaurants/RestaurantList.vue'),
    meta: { requiresAuth: false, showBottomNav: true },
  },
  {
    path: '/restaurant-signup',
    name: 'PartnerWithUs',
    component: () => import('@/views/partner/PartnerWithUs.vue'),
    meta: { requiresAuth: false, showMobileMenu: true },
  },
  {
    path: '/partner-with-us',
    redirect: '/restaurant-signup',
  },
  {
    path: '/smart-dine-in',
    name: 'SmartDineInHome',
    component: () => import('@/views/smart-dine-in/SmartDineInHome.vue'),
    meta: { requiresAuth: false, showBottomNav: true },
  },
  {
    path: '/cart',
    name: 'Cart',
    component: () => import('@/views/cart/CartView.vue'),
    meta: { requiresAuth: false, showBottomNav: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

let authInitialized = false

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (
    SMART_DINE_IN_ONLY &&
    BLOCKED_WHEN_SMART_DINE_IN_ONLY.some(path => to.path === path || to.path.startsWith(`${path}/`))
  ) {
    next({ path: '/smart-dine-in', query: { flow: 'smart-dine-in' } })
    return
  }

  if (!authInitialized) {
    authInitialized = true
    try {
      await authStore.initializeAuth()
      // On refresh: if we have stored reservation, validate with check_active_reservation_new; if not 200, clear
      if (authStore.isAuthenticated) {
        const raw = localStorage.getItem('active_reservation_info')
        if (raw) {
          try {
            const info = JSON.parse(raw) as { reservationId?: string }
            const reservationId = info?.reservationId
            const id = reservationId ? Number(reservationId) : NaN
            if (!Number.isNaN(id)) {
              const checkRes = await reservationApi.checkActiveReservationNew({
                active_reservation_id: id,
              })
              const code = (checkRes as { code?: number }).code
              const isOk =
                !(checkRes as { error?: unknown }).error && (code === undefined || code === 200)
              if (!isOk) {
                authStore.clearActiveReservation()
                if (to.query.reservation_id) {
                  const nextQuery = { ...to.query }
                  delete nextQuery.reservation_id
                  next({ path: to.path, query: nextQuery, replace: true })
                  return
                }
              }
            }
          } catch {
            authStore.clearActiveReservation()
            if (to.query.reservation_id) {
              const nextQuery = { ...to.query }
              delete nextQuery.reservation_id
              next({ path: to.path, query: nextQuery, replace: true })
              return
            }
          }
        }
      }
    } catch (error) {
      console.warn('Auth initialization failed:', error)
    }
  }

  const hasUserData = localStorage.getItem('user')
  const currentToken =
    localStorage.getItem('customer_access_token') || sessionStorage.getItem('customer_access_token')

  if (hasUserData && authStore.isAuthenticated && currentToken) {
    if (isTokenExpired(currentToken)) {
      try {
        await authStore.refreshToken()
      } catch (error) {
        console.warn('Token refresh failed during navigation:', error)
      }
    }
  }

  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      next({
        name: 'Login',
        query: { redirect: to.fullPath },
      })
      return
    }
  }

  // Login/Register routes are now routed modals driven by `drawer` query.
  // If user is already authenticated, keep existing behavior (go to redirect or home).
  if (to.name === 'Login' || to.name === 'Register') {
    if (authStore.isAuthenticated) {
      const redirectTo = (from.query.redirect as string) || '/'
      next({ path: redirectTo })
      return
    }

    const drawer = to.name === 'Login' ? 'login' : 'register'
    next({
      name: 'Home',
      query: { ...to.query, drawer },
    })
    return
  }

  if (to.meta.title) {
    document.title = `${to.meta.title} - SCANME`
  }

  next()
})

export default router
